import { DesignDetailResource, usePatchDesignsByDesignMutation } from '@/api/serverApi'
import { MinimalTextInput, useErrorHandler } from '@cfs-fixings/shared'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { useEffect, useImperativeHandle, useRef } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'
import { useDispatch } from 'react-redux'
import { setActiveDesign } from '@/stores/active-design'

const renameDesignSchema = v.object({
  name: v.string([v.minLength(1, 'Name is required')]),
})

type RenameDesignFormInputs = v.Input<typeof renameDesignSchema>

type PropTypes = {
  design: DesignDetailResource,
  isFormActive?: boolean,
  onCompleted?: (project: DesignDetailResource) => void,
}

function RenameDesignForm ({ design, isFormActive = false, onCompleted }: PropTypes) {
  const renameRef = useRef<HTMLInputElement>(null)

  const errorHandler = useErrorHandler()
  const dispatch = useDispatch()

  const { register, handleSubmit, formState: { errors }, setError, watch } = useForm<RenameDesignFormInputs>({
    resolver: valibotResolver(renameDesignSchema),
    defaultValues: {
      name: design.name,
    },
  })

  const name = watch('name')

  const { ref, ...restOfRegistration } = register('name')

  useImperativeHandle(ref, () => renameRef.current)

  const [renameMutation] = usePatchDesignsByDesignMutation()

  const onSubmit: SubmitHandler<RenameDesignFormInputs> = (data) => {
    renameMutation({
      design: design.id,
      body: data,
    })
      .unwrap()
      .then((updatedDesign) => {
        const completeDesign = {
          ...design,
          ...updatedDesign,
          name: data.name,
        }
        dispatch(setActiveDesign(completeDesign))
        onCompleted?.(completeDesign)
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        }
      })
  }

  useEffect(() => {
    if (isFormActive) {
      renameRef.current?.focus()
    }
  }, [isFormActive])

  return (
    <>
      {
        isFormActive
          ? (
            <div className="min-w-[800px]">
              <MinimalTextInput
                ref={renameRef}
                {...restOfRegistration}
                defaultValue={design.name}
                error={errors?.name?.message}
                onBlur={handleSubmit(onSubmit)}
              />
            </div>
          )
          : (
            <p className="font-medium text-[14px] max-w-xs md:max-w-lg truncate">
              {name ?? design?.name}
            </p>
          )
      }
    </>
  )
}

export default RenameDesignForm
